import { Injectable, inject, signal, Injector } from '@angular/core';
import { CognitoService } from './cognito.service';

@Injectable({
  providedIn: 'root'
})
export class UserRoleService {
  // Use Injector instead of direct injection to break circular dependency
  private injector = inject(Injector);
  private cognito: CognitoService | null = null;
  
  // Signal for current user role
  private _currentRole = signal<string | null>(null);
  
  // Public readonly signal
  readonly currentRole = this._currentRole.asReadonly();

  constructor() {
    // Defer initialization to avoid circular dependency
    setTimeout(() => {
      this.initService();
    });
  }

  private initService(): void {
    // Get CognitoService from injector when needed
    this.cognito = this.injector.get(CognitoService);
    // Load the user role after getting the service
    this.loadUserRole();
  }

  /**
   * Load the current user role from Cognito
   */
  async loadUserRole(): Promise<string | null> {
    if (!this.cognito) {
      console.warn('CognitoService not available yet');
      return null;
    }

    try {
      const userData = await this.cognito.adminGetUser();
      if (userData && typeof userData === 'object') {
        const userDataObj = userData as Record<string, any>;
        // Get role and convert to lowercase if it exists
        const roleValue = userDataObj['custom:role'] || null;
        const role = roleValue ? roleValue.toLowerCase() : null;
        
        // Store the lowercase role in the signal
        this._currentRole.set(role);
        
        return role;
      }
      return null;
    } catch (error) {
      console.error('Error loading user role:', error);
      return null;
    }
  }

  /**
   * Check if the current user has the specified role
   * Role comparison is case-insensitive
   */
  hasRole(role: string): boolean {
    const currentRole = this._currentRole();
    if (!currentRole || !role) return false;
    return currentRole === role.toLowerCase();
  }

  /**
   * Check if the current user has any of the specified roles
   * Role comparison is case-insensitive
   */
  hasAnyRole(roles: string[]): boolean {
    const currentRole = this._currentRole();
    if (!currentRole || !roles || roles.length === 0) return false;
    
    // Convert all roles to lowercase for comparison
    const lowerCaseRoles = roles.map(r => r.toLowerCase());
    return lowerCaseRoles.includes(currentRole);
  }

  /**
   * Clear the current role (for logout)
   */
  clearRole(): void {
    this._currentRole.set(null);
  }
} 